import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { FadeInSection } from '../components/FadeInSection';

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '+27000000000';
const IS_WA_PLACEHOLDER = !process.env.REACT_APP_WHATSAPP_NUMBER || WHATSAPP_NUMBER === '+27000000000';

const PHONE_NUMBER = process.env.REACT_APP_PHONE_NUMBER || '+27210000000';
const PHONE_DISPLAY = PHONE_NUMBER.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');
const AFTER_HOURS = process.env.REACT_APP_AFTER_HOURS_NUMBER || '+27820000000';
const AFTER_HOURS_DISPLAY = AFTER_HOURS.replace(/(\+27)(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');

// Animated count-up component
function CountUp({ target, suffix = '' }: { target: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const [count, setCount] = React.useState(0);

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 1200;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export function HomepagePage() {
  const [waToast, setWaToast] = useState(false);
  const handleWaClick = (e: React.MouseEvent) => {
    if (IS_WA_PLACEHOLDER) {
      e.preventDefault();
      setWaToast(true);
      setTimeout(() => setWaToast(false), 4000);
    }
  };

  useEffect(() => {
    document.title = 'IKhanGynae | Specialist OB/GYN in Cape Town Southern Suburbs';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'Dr Imraan Khan, specialist Obstetrician and Gynaecologist in Cape Town Southern Suburbs. Book an appointment online or via WhatsApp.'
      );
    }
  }, []);

  const services = [
    {
      title: 'Obstetrics',
      bullets: [
        'Antenatal care — routine and high-risk',
        'Twin and multiple pregnancies',
        'Gestational diabetes management',
      ],
    },
    {
      title: 'Gynaecology',
      bullets: [
        'General gynaecological consultations',
        'Endometriosis diagnosis and management',
        'Menopause management and HRT',
      ],
    },
    {
      title: 'Laparoscopic Surgery',
      bullets: [
        'Diagnostic and operative laparoscopy',
        'Hysteroscopy',
        'Minimally invasive procedures',
      ],
    },
    {
      title: 'Antenatal Care',
      bullets: [
        'Personalised pregnancy monitoring',
        'Routine and specialised scans',
        'Pre-eclampsia monitoring',
      ],
    },
  ];

  const whyChoose = [
    {
      heading: 'Evidence-Based Care',
      body: 'FCOG(SA) specialist training with over a decade of clinical experience in obstetrics and gynaecology.',
    },
    {
      heading: 'High-Risk Obstetrics',
      body: 'Special interest in complex pregnancies including twins, pre-eclampsia, and gestational diabetes.',
    },
    {
      heading: 'Minimally Invasive Surgery',
      body: 'Trained in laparoscopic techniques, which are generally associated with shorter recovery times compared to open surgery.',
    },
    {
      heading: 'Always Learning',
      body: 'Dedicated to staying current with the latest surgical techniques and medical research to deliver the best possible care.',
    },
  ];

  return (
    <div>
      {/* ── Hero Section ── */}
      <section
        className="relative flex items-center py-20 px-4 overflow-x-hidden"
        style={{
          background: 'linear-gradient(135deg, var(--color-navy) 0%, #243447 100%)',
          minHeight: '80vh',
        }}
      >
        {/* Logo watermark */}
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />

        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="flex flex-col-reverse md:flex-row items-center gap-12">

            {/* Left — text */}
            <motion.div
              className="flex-1 text-white"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <p
                className="text-sm font-body mb-3 tracking-widest uppercase italic"
                style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontSize: '1rem' }}
              >
                Specialist Obstetrician &amp; Gynaecologist
              </p>
              <h1
                className="leading-tight mb-4 font-heading"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 300,
                  fontSize: 'clamp(2rem, 5vw, 4rem)',
                  lineHeight: 1.1,
                  color: '#fff',
                }}
              >
                Compassionate Care<br />for Women's Health
              </h1>
              <p
                className="text-base mb-2 font-medium"
                style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-body)' }}
              >
                Dr Imraan Khan FCOG(SA)
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-8 max-w-xl" style={{ fontFamily: 'var(--font-body)' }}>
                Dr Imraan Khan is a specialist Obstetrician and Gynaecologist based in Cape Town Southern Suburbs, Western Cape. With over a decade of experience, he is committed to providing personalised, evidence-based care to women at every stage of life.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={IS_WA_PLACEHOLDER ? '#' : `https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20I%27d%20like%20to%20book%20an%20appointment`}
                  target={IS_WA_PLACEHOLDER ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  onClick={handleWaClick}
                  className="inline-block px-6 py-3 rounded-md font-semibold text-center transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: 'var(--color-gold)',
                    color: 'var(--color-navy)',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  Book via WhatsApp
                </a>
                <Link
                  to="/register"
                  className="inline-block px-6 py-3 rounded-md font-semibold text-white border-2 border-white hover:bg-white transition-colors text-center"
                  style={{
                    fontFamily: 'var(--font-body)',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-navy)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = '#fff'; }}
                >
                  Register as Patient
                </Link>
              </div>
            </motion.div>

            {/* Right — photos */}
            <motion.div
              className="flex-shrink-0 flex flex-col items-center gap-4"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <div
                className="relative"
                style={{
                  borderRadius: '1.5rem',
                  padding: '4px',
                  background: 'linear-gradient(135deg, var(--color-gold) 0%, transparent 60%)',
                }}
              >
                <img
                  src="/images/hero-headshot.jpg?v=2"
                  alt="Dr Imraan Khan"
                  className="object-cover shadow-2xl"
                  style={{
                    width: 480,
                    height: 560,
                    borderRadius: '1.25rem',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Stats Band ── */}
      <section
        className="py-12 px-4"
        style={{ backgroundColor: 'var(--color-primary)' }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center text-white">
            {[
              { number: 10, suffix: '+', label: 'Years Experience', sub: 'in Obstetrics & Gynaecology' },
              { number: 2, suffix: '', label: 'Hospitals', sub: "JKD at Melomed Gatesville & Melomed Mitchell's Plain" },
              { number: -1, suffix: '', label: 'Medical Aids', sub: 'All major medical aids accepted' },
            ].map((stat, i) => (
              <FadeInSection key={stat.label} delay={i * 0.1}>
                <p
                  className="font-heading leading-none mb-1"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                    fontWeight: 300,
                    color: 'var(--color-gold)',
                  }}
                >
                  {stat.number >= 0 ? <CountUp target={stat.number} suffix={stat.suffix} /> : '✓'}
                </p>
                <p className="font-heading text-xl text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                  {stat.label}
                </p>
                <p className="text-white/70 text-xs mt-1 font-body" style={{ fontFamily: 'var(--font-body)' }}>
                  {stat.sub}
                </p>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services Cards ── */}
      <section className="py-20 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-2"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
                fontWeight: 400,
              }}
            >
              Areas of Expertise
            </h2>
            <p className="text-center text-sm mb-12 font-body" style={{ color: 'var(--color-navy)', opacity: 0.6, fontFamily: 'var(--font-body)' }}>
              Comprehensive women's health care under one roof
            </p>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((card, i) => (
              <FadeInSection key={card.title} delay={i * 0.08}>
                <div
                  className="rounded-xl p-6 flex flex-col h-full shadow-sm border-l-4 hover:shadow-md transition-shadow"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    borderLeftColor: 'var(--color-primary)',
                  }}
                >
                  <h3
                    className="font-heading text-xl mb-3"
                    style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                  >
                    {card.title}
                  </h3>
                  <ul className="flex-1 space-y-1.5 mb-4">
                    {card.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-sm font-body"
                        style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}
                      >
                        <span style={{ color: 'var(--color-primary)', marginTop: 2 }}>•</span>
                        <span className="opacity-80">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/services"
                    className="text-sm font-medium font-body hover:underline"
                    style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
                  >
                    Learn more →
                  </Link>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── About Snippet ── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <FadeInSection className="flex-shrink-0 w-full md:w-96">
              <img
                src="/images/services-portrait.jpg?v=2"
                alt="Dr Imraan Khan"
                className="w-full rounded-2xl shadow-lg object-cover"
                style={{ maxHeight: 420 }}
              />
            </FadeInSection>
            <FadeInSection delay={0.15} className="flex-1">
              <div
                className="pl-5 border-l-4"
                style={{ borderLeftColor: 'var(--color-gold)' }}
              >
                <p
                  className="font-heading leading-none mb-1"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '5rem',
                    color: 'var(--color-gold)',
                    lineHeight: 0.8,
                    opacity: 0.6,
                  }}
                >
                  "
                </p>
                <p
                  className="font-heading italic text-xl leading-relaxed mb-4"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--color-navy)',
                    fontStyle: 'italic',
                    fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
                  }}
                >
                  With over a decade of experience in both obstetrics and gynaecology, Dr Khan is committed to providing personalised, evidence-based care to women at every stage of life — from adolescence through to menopause and beyond.
                </p>
                <p className="text-sm font-body mb-6" style={{ color: 'var(--color-navy)', opacity: 0.65, fontFamily: 'var(--font-body)' }}>
                  He has a special interest in high-risk obstetrics, laparoscopic surgery, and the initial assessment and referral of gynaecological cancers. Dr Khan consults at JKD at Melomed Gatesville and Melomed Mitchell's Plain.
                </p>
                <Link
                  to="/about"
                  className="text-sm font-medium font-body hover:underline"
                  style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-body)' }}
                >
                  Read more about Dr Khan →
                </Link>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ── Why Choose Dr Khan ── */}
      <section className="py-20 px-4" style={{ backgroundColor: 'var(--color-primary-light)' }}>
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-12"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
                fontWeight: 400,
              }}
            >
              Why Choose Dr Khan
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((item, i) => (
              <FadeInSection key={item.heading} delay={i * 0.08}>
                <div className="text-center p-6">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                  >
                    <span className="font-heading text-xl font-medium" style={{ fontFamily: 'var(--font-heading)' }}>
                      {i + 1}
                    </span>
                  </div>
                  <h3
                    className="font-heading text-lg mb-2"
                    style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                  >
                    {item.heading}
                  </h3>
                  <p className="text-sm font-body opacity-70" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                    {item.body}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      {/* Placeholder — replace with real testimonials before go-live */}
      <section className="py-20 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-5xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-12"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
                fontWeight: 400,
              }}
            >
              What Our Patients Say
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "Dr Khan delivered both my children. His calm, thorough approach made a stressful time feel manageable.",
                attribution: "Patient, Cape Town Southern Suburbs",
              },
              {
                quote: "From my first antenatal visit to delivery, I felt completely supported. I couldn't recommend Dr Khan more highly.",
                attribution: "Patient, Cape Town",
              },
              {
                quote: "Professional, caring and incredibly knowledgeable. He took the time to explain everything clearly.",
                attribution: "Patient, Paarl",
              },
            ].map((t, i) => (
              <FadeInSection key={i} delay={i * 0.1}>
                <div
                  className="rounded-xl p-6 shadow-sm"
                  style={{ backgroundColor: '#fff', borderTop: `3px solid var(--color-gold)` }}
                >
                  <p
                    className="font-heading italic text-2xl leading-none mb-3"
                    style={{ color: 'var(--color-gold)', fontFamily: 'var(--font-heading)' }}
                  >
                    "
                  </p>
                  <p
                    className="font-heading italic leading-relaxed mb-4"
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontStyle: 'italic',
                      color: 'var(--color-navy)',
                      fontSize: '1.05rem',
                    }}
                  >
                    {t.quote}
                  </p>
                  <p className="text-xs font-body opacity-60" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                    — {t.attribution}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Medical Aids Strip ── */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <FadeInSection>
            <h2
              className="font-heading mb-8"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                color: 'var(--color-navy)',
                fontWeight: 400,
              }}
            >
              Medical Aid Friendly
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {['Discovery', 'Bonitas', 'Medihelp', 'Momentum', 'Fedhealth'].map((aid) => (
                <span
                  key={aid}
                  className="px-5 py-2 rounded-full text-sm font-body font-medium transition-colors cursor-default"
                  style={{
                    backgroundColor: 'var(--color-primary-light)',
                    color: 'var(--color-primary)',
                    fontFamily: 'var(--font-body)',
                    border: '1px solid var(--color-primary)',
                  }}
                >
                  {aid}
                </span>
              ))}
            </div>
            <p className="text-xs mt-4 opacity-50 font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
              Dr Khan is registered with most major medical aids. If yours isn't listed above, please contact the practice — we'll confirm your cover.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* ── Hours + Location ── */}
      <section className="py-20 px-4" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <h2
              className="font-heading text-center mb-10"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--color-navy)',
                fontWeight: 400,
              }}
            >
              Find Us
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {/* Hours card */}
            <FadeInSection>
              <div className="rounded-xl p-6 shadow-sm h-full" style={{ backgroundColor: '#fff' }}>
                <h3
                  className="font-heading text-xl mb-4"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                >
                  Consulting Hours
                </h3>
                <ul className="space-y-2 text-sm font-body" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  <li className="flex justify-between">
                    <span>Monday – Thursday</span>
                    <span className="font-medium">08:00 – 17:00</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Friday</span>
                    <span className="font-medium">08:00 – 13:00</span>
                  </li>
                </ul>
                <p className="mt-4 text-xs font-body opacity-60" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  After hours: <a href={`tel:${AFTER_HOURS}`} className="font-medium" style={{ color: 'var(--color-primary)' }}>{AFTER_HOURS_DISPLAY}</a>
                </p>
              </div>
            </FadeInSection>

            {/* Address card */}
            <FadeInSection delay={0.1}>
              <div className="rounded-xl p-6 shadow-sm h-full" style={{ backgroundColor: '#fff' }}>
                <h3
                  className="font-heading text-xl mb-4"
                  style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-navy)', fontWeight: 500 }}
                >
                  Location
                </h3>
                <p className="text-sm font-body mb-1" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>JKD at Melomed Gatesville &amp; Melomed Mitchell's Plain</p>
                <p className="text-sm font-body mb-4" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>Cape Town, Western Cape</p>
                <p className="text-xs font-body opacity-60" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-body)' }}>
                  Free parking on-site.
                </p>
                <p className="text-xs font-body mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                  Phone: <a href={`tel:${PHONE_NUMBER}`} className="font-medium" style={{ color: 'var(--color-primary)' }}>{PHONE_DISPLAY}</a>
                </p>
                <p className="text-xs font-body mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                  Gatesville: <a href="tel:0216371343" className="font-medium" style={{ color: 'var(--color-primary)' }}>021 637 1343</a>
                </p>
                <p className="text-xs font-body mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                  Mitchell's Plain: <a href="tel:0213928141" className="font-medium" style={{ color: 'var(--color-primary)' }}>021 392 8141</a>
                </p>
              </div>
            </FadeInSection>
          </div>

          {/* Google Maps embed */}
          <div className="rounded-2xl overflow-hidden shadow-md">
            <iframe
              title="IKhanGynae location map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=18.6115%2C-34.0626%2C18.6515%2C-34.0226&layer=mapnik&marker=-34.0426%2C18.6315"
              width="100%"
              height="350"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* ── Final CTA Band ── */}
      <section
        className="py-16 px-4 text-center relative overflow-hidden"
        style={{ backgroundColor: 'var(--color-navy)' }}
      >
        <img
          src="/images/logo-symbol-only.png"
          alt=""
          aria-hidden="true"
          className="absolute pointer-events-none select-none"
          style={{ width: 300, right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.07 }}
        />
        <div className="relative z-10">
          <FadeInSection>
            <h2
              className="font-heading text-white mb-4"
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: 300,
              }}
            >
              Ready to Book?
            </h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto font-body" style={{ fontFamily: 'var(--font-body)' }}>
              Contact Dr Khan's practice today — or register online as a new patient.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%2C%20I%27d%20like%20to%20book%20an%20appointment`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity font-body"
                style={{
                  backgroundColor: 'var(--color-gold)',
                  color: 'var(--color-navy)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                Book via WhatsApp
              </a>
              <Link
                to="/register"
                className="inline-block px-8 py-3 rounded-md font-semibold text-white border-2 border-white hover:bg-white transition-colors font-body"
                style={{ fontFamily: 'var(--font-body)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-navy)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = '#fff'; }}
              >
                Register Online
              </Link>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* WhatsApp placeholder toast */}
      <AnimatePresence>
        {waToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 right-6 z-50 max-w-xs px-4 py-3 rounded-lg shadow-lg text-sm font-body"
            style={{ backgroundColor: 'var(--color-navy)', color: '#fff', fontFamily: 'var(--font-body)' }}
          >
            WhatsApp booking coming soon — please call us or use the{' '}
            <a href="/book" className="underline font-semibold" style={{ color: 'var(--color-gold)' }}>booking form</a>.
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
